import React, { useCallback, useState, useMemo } from 'react';

import { api } from 'services/api';
import { shouldTreatSuggestedAsCompositionOriginal } from 'shared/budget-component-type';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Flex,
  Grid,
  FormControl,
  FormLabel,
  Heading,
  Textarea,
  Text,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import {
  Composition,
  Input,
} from 'pages/Budget/Add/SubPages/Planning/Common/types';

type BudgetComponent = Composition | Input;

interface ModalBudgetComponentObservationProps extends ModalRootProps {
  data: {
    budgetId: number;
    component: BudgetComponent;
  };
}

const ModalBudgetCompositionObservation: React.FC<
  ModalBudgetComponentObservationProps
> = ({
  onConfirm,
  handleClose,
  data: { budgetId, component },
  ...restProps
}) => {
  const [observation, setObservation] = useState(component.observation);
  const [loading, setLoading] = useState(false);

  const isComposition = (c: BudgetComponent): c is Composition => {
    return shouldTreatSuggestedAsCompositionOriginal({
      type: c.type,
      total: c.total,
    });
  };

  const isInput = (c: BudgetComponent): c is Input => {
    return c.type === 'input_original';
  };

  const handleSubmitComponents = useCallback(async () => {
    setLoading(true);

    try {
      await api.put(`budget/${budgetId}/component/${component.id}`, {
        type: 'composition_original',
        component: {
          quantity: component.quantity,
          observation,
          ...(isComposition(component)
            ? {
                composition_price_id: component.composition_price?.id,
              }
            : {}),
          ...(isInput(component)
            ? {
                input_price_id: component.input_price?.id,
              }
            : {}),
        },
      });

      toast({
        description: 'Serviço alterado com sucesso!',
        status: 'success',
      });

      if (onConfirm) onConfirm();
      handleClose();
    } catch (err) {
      toast({
        description:
          err.response?.data?.message || 'Houve um erro ao editar o serviço.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [budgetId, component, observation, handleClose, onConfirm]);

  const description = useMemo(() => {
    if (isComposition(component)) {
      return (
        component.composition_price?.custom_description ||
        component.composition_price?.composition.description
      );
    }

    if (isInput(component)) {
      return (
        component.input_price?.custom_description ||
        component.input_price?.input.description
      );
    }

    return '';
  }, [component]);

  return (
    <Modal {...restProps} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Editar Serviço</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Flex
            w="100%"
            as="form"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{
              '> *': {
                marginY: 1,
              },
            }}
          >
            <Heading
              fontSize="medium"
              fontWeight="600"
              width="100%"
              textAlign="left"
              mt={-2}
              mb={4}
            >
              Descrição
              <Text fontWeight="400" fontSize="sm" mt={2}>
                {description}
              </Text>
            </Heading>

            <Grid
              w="100%"
              templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
              columnGap={2}
              rowGap={4}
            >
              <FormControl>
                <FormLabel>Memória de cálculo</FormLabel>

                <Textarea
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Digite a memória de cálculo"
                  size="lg"
                  resize="none"
                />
              </FormControl>
            </Grid>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            colorScheme="green"
            onClick={handleSubmitComponents}
          >
            Confirmar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetCompositionObservation;
